import {createActions} from 'redux-actions';
import {type AxiosPromise} from 'axios';
import FormData from 'form-data';
import {FULFILLED, PENDING, REJECTED} from 'redux-promise-middleware';

import {type PaymentType, type PaymentOsType} from 'constants/flow/flowTypes';
import {CHECKOUT} from 'constants/actions';
import {PAYMENT_METHODS} from 'constants/checkout/paymentMethods';
import {ENDPOINTS} from 'constants/endpoints';
import {PAYMENTS_METHODS} from 'features/paymentsMethods/constants';
import {getConvertedPaymentObject} from 'libs/checkout/checkout';
import {instanceGusaBackend} from 'libs/api';
import {extractErrorMessagesFromResponse} from 'libs/utils/utils';

export const initialState = {
    code: '',
    selected: '',
    title: '',
    card: {
        code: {
            name: '',
            size: 0,
        },
        gaps: [],
        lengths: [],
        niceType: '',
        prefixPattern: '',
        type: '',
        type2: '',
    },
    cardNumber: '',
    month: '',
    year: '',
    cvv: '',
    inProgress: false,
    paymentToken: '',
    paymentMethodToken: '',
    sessionId: '',
    saveCard: false,
    savedCards: [],
    error: '',
    orderErrors: [],
};
// TODO: Split reducer

export const {
    checkout: {savePayment, payWithSavedCard, payWithNewCard, setPaymentOsError},
} = createActions({
    CHECKOUT: {
        /**
         * Save payment
         * @param {PaymentOsType | PaymentType} paymentInfo - payment method info
         * @param {string} formKey - form key
         * @return {AxiosPromise} - response promise
         */
        SAVE_PAYMENT: (paymentInfo: PaymentOsType | PaymentType, formKey: string): AxiosPromise => {
            const dataMap = getConvertedPaymentObject(paymentInfo);

            const data = new FormData();
            data.append('form_key', formKey);

            const appendData = (index: string) => data.append(`payment[${index}]`, dataMap[index]);

            if (paymentInfo.isValidConditions === '1')
                ['terms', 'installments_no'].forEach(appendData);

            const methodsFieldsMap = {
                [PAYMENT_METHODS.PAYMENT_OS]: ['method', 'save_card', 'token'],
                [PAYMENT_METHODS.RECURLY]: ['method', 'token'],
                [PAYMENTS_METHODS.METHOD_KLARNA]: ['method', 'token', 'session_id'],
                [PAYMENTS_METHODS.METHOD_STRIPE]: ['method', 'payment_method_id'],
                default: [
                    'method',
                    'cc_type',
                    'cc_number',
                    'cc_exp_month',
                    'cc_exp_year',
                    'cc_cid',
                ],
            };
            (methodsFieldsMap[paymentInfo.code] || methodsFieldsMap.default).forEach(appendData);

            return instanceGusaBackend({
                headers: {'Content-Type': 'multipart/form-data'},
                method: 'post',
                url: ENDPOINTS.CHECKOUT_SET_PAYMENT,
                data,
            });
        },
        /**
         * Submit to order review with saved card
         * @param {string} token - saved card token
         * @return {string} - saved card token
         */
        PAY_WITH_SAVED_CARD: (token: string): string => token,
        /**
         * Submit to order review with new card
         * @param {{isForSave: boolean, cardHolderName: string}} payload - user data for saving card
         * @return {{isForSave: boolean, cardHolderName: string}} - user data for saving card
         */
        PAY_WITH_NEW_CARD: (payload: {isForSave: boolean, cardHolderName: string}) => payload,
        /**
         * Tell us that something is wrong with payment os
         * @param {{message: string, eventType: string}} data - payment os error message data
         * @return {{message: string, eventType: string}} - payment os error message data
         */
        SET_PAYMENT_OS_ERROR: (data: {message: string, eventType: string}) => data,
    },
});

/**
 * Set active payment type
 *
 * @param {object|null} state Current state
 * @param {object} action Received action
 * @returns {object} Active shipping object
 */
export default function paymentMethodActive( // eslint-disable-line complexity
    state: PaymentType = initialState,
    action: ActionType<*>,
): PaymentType {
    switch (action.type) {
        case CHECKOUT.SET_ACTIVE_PAYMENT:
            return {
                ...state,
                error: '',
                ...action.payload,
            };

        case `${savePayment}_${PENDING}`:
        case `${payWithSavedCard}`:
        case `${payWithNewCard}`:
            return {...state, error: '', inProgress: true};

        case `${savePayment}_${FULFILLED}`: {
            let newState = {inProgress: false};
            const {card} = action.payload.data || {};
            if (card && card.cc_last4)
                newState = {
                    month: card.cc_exp_month,
                    year: card.cc_exp_year,
                    cardNumber: `****-${card.cc_last4}`,
                    level: card.level,
                    type: card.type,
                    ...newState,
                };
            return {...state, ...newState};
        }

        case `${savePayment}_${REJECTED}`:
            return {...state, error: action.payload.response.data.message, inProgress: false};

        case CHECKOUT.SET_PAYMENT_METHOD_TOKEN:
            return {
                ...state,
                paymentMethodToken: action.payload.token,
                sessionId: action.payload.sessionId,
            };

        case CHECKOUT.SET_PAYMENT_ERROR:
            return {...state, error: action.payload, inProgress: false};

        case CHECKOUT.SAVE_CARD:
            return {...state, saveCard: action.payload};

        case CHECKOUT.SET_IN_PROGRESS:
            return {...state, inProgress: action.payload};

        case `${CHECKOUT.SAVE_ORDER}_${REJECTED}}`: {
            const orderErrors = extractErrorMessagesFromResponse(action.payload.response);
            return {...state, orderErrors};
        }

        case 'GET_PAYMENT_METHODS_PENDING':
            return initialState;

        default:
            return state;
    }
}
